// T074: Implement useCreatePost hook
// T075: Add RC check before publishing
// T078: Add optimistic update to feed
// T086: Implement useEditPost hook
// T090: Optimistically update UI on edit
// T091: Implement useDeletePost hook
// T094: Remove post from feed/profile optimistically

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { getPost, SteemitPost } from '@/services/feeds/feedsClient'
import { queryKeys } from '@/services/queryClient'
import { Post } from '@/types/post'
import { getPostUrl } from '@/utils/permlink'
import { generateSummary, calculateReadTime } from '@/utils/titleGenerator'
import {
  createPost,
  updatePost,
  deletePost,
  type CreatePostParams,
  type UpdatePostParams,
  type DeletePostParams,
} from '@/services/steemit/postService'
import { getUserRC } from '@/services/steemit/rcService'

const MIN_RC_PERCENTAGE = 20 // Minimum RC required to post

/**
 * Hook for fetching a single post
 */
interface UsePostOptions {
  author: string
  permlink: string
  enabled?: boolean
}

export function usePost({ author, permlink, enabled = true }: UsePostOptions) {
  return useQuery({
    queryKey: queryKeys.post(author, permlink),
    queryFn: async (): Promise<Post | null> => {
      const response = await getPost(author, permlink)

      const post = response?.result
      if (!post || !post.author) {
        return null
      }

      return transformSteemitPost(post)
    },
    enabled: enabled && !!author && !!permlink,
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Transform Steemit API post response to Post type
 */
function transformSteemitPost(post: SteemitPost): Post {
  // Parse payout values
  const pendingPayout = parseFloat(post.pending_payout_value?.replace(' SBD', '').replace(' HBD', '') || '0')
  const totalPayout = parseFloat(post.total_payout_value?.replace(' SBD', '').replace(' HBD', '') || '0')
  const curatorPayout = parseFloat(post.curator_payout_value?.replace(' SBD', '').replace(' HBD', '') || '0')
  const payout = pendingPayout > 0 ? pendingPayout : totalPayout + curatorPayout

  // Calculate reputation
  const rawRep = post.author_reputation || 0
  const reputation = rawRep > 0 ? Math.floor(Math.log10(rawRep) * 9 - 56) : 25

  // Parse JSON metadata
  let tags: string[] = []
  let images: string[] = []
  try {
    const metadata = JSON.parse(post.json_metadata || '{}')
    tags = metadata.tags || []
    images = metadata.image || []
  } catch {
    tags = []
    images = []
  }

  return {
    linkId: post.post_id || 0,
    author: post.author,
    permlink: post.permlink,
    title: post.title || '',
    body: post.body || '',
    tags,
    category: post.category,
    images,
    created: new Date(post.created + 'Z'),
    lastUpdate: new Date(post.last_update + 'Z'),
    cashoutTime: new Date(post.cashout_time + 'Z'),
    community: null,
    isPinned: false,
    isMuted: false,
    upvoteCount: post.active_votes?.length || 0,
    downvoteCount: 0,
    commentCount: post.children || 0,
    resteemCount: 0,
    payout,
    payoutComments: 0,
    promoted: parseFloat(post.promoted?.replace(' SBD', '').replace(' HBD', '') || '0'),
    maxAcceptedPayout: parseFloat(post.max_accepted_payout?.replace(' SBD', '').replace(' HBD', '') || '0'),
    percentSteemDollars: post.percent_steem_dollars || 10000,
    authorReputation: reputation,
    authorTitle: null,
    parentAuthor: post.root_author || '',
    parentPermlink: post.root_permlink || post.category,
    resteemedBy: null,
    url: post.url || getPostUrl(post.category, post.author, post.permlink),
    summary: generateSummary(post.body),
    readTime: calculateReadTime(post.body),
  }
}

/**
 * Hook for creating a new post
 */
export function useCreatePost() {
  const queryClient = useQueryClient()
  const { user, updateRcPercentage } = useAuthStore()

  return useMutation({
    mutationFn: async (params: Omit<CreatePostParams, 'author' | 'postingKey'>) => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      // T075: Check RC before publishing
      if (user.rcPercentage < MIN_RC_PERCENTAGE) {
        throw new Error(
          `Insufficient Resource Credits. You need at least ${MIN_RC_PERCENTAGE}% RC to post. Current: ${user.rcPercentage.toFixed(1)}%`
        )
      }

      // Create the post
      const result = await createPost({
        ...params,
        author: user.username,
        postingKey: user.postingKey,
      })

      if (!result.success) {
        throw new Error(result.error || 'Failed to create post')
      }

      // Refresh RC after posting
      try {
        const rc = await getUserRC(user.username)
        updateRcPercentage(rc.percentage)
      } catch (error) {
        console.error('Failed to refresh RC:', error)
      }

      return result
    },
    onSuccess: () => {
      // T078: Invalidate feed queries to show new post
      queryClient.invalidateQueries({ queryKey: ['feed'] })
      queryClient.invalidateQueries({ queryKey: ['user-posts'] })
    },
  })
}

/**
 * Hook for updating an existing post
 */
export function useUpdatePost() {
  const queryClient = useQueryClient()
  const { user, updateRcPercentage } = useAuthStore()

  return useMutation({
    mutationFn: async (params: Omit<UpdatePostParams, 'author' | 'postingKey'>) => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      // Check RC before updating
      if (user.rcPercentage < MIN_RC_PERCENTAGE) {
        throw new Error(
          `Insufficient Resource Credits. You need at least ${MIN_RC_PERCENTAGE}% RC to update posts.`
        )
      }

      // Update the post
      const result = await updatePost({
        ...params,
        author: user.username,
        postingKey: user.postingKey,
      })

      if (!result.success) {
        throw new Error(result.error || 'Failed to update post')
      }

      // Refresh RC after updating
      try {
        const rc = await getUserRC(user.username)
        updateRcPercentage(rc.percentage)
      } catch (error) {
        console.error('Failed to refresh RC:', error)
      }

      return result
    },
    onSuccess: (_data, variables) => {
      // T090: Invalidate queries to show updated post
      queryClient.invalidateQueries({ queryKey: ['feed'] })
      queryClient.invalidateQueries({ queryKey: ['user-posts'] })
      queryClient.invalidateQueries({
        queryKey: ['post', variables.permlink],
      })
    },
  })
}

/**
 * Hook for deleting a post
 */
export function useDeletePost() {
  const queryClient = useQueryClient()
  const { user, updateRcPercentage } = useAuthStore()

  return useMutation({
    mutationFn: async (params: Omit<DeletePostParams, 'author' | 'postingKey'>) => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      // Check RC before deleting
      if (user.rcPercentage < MIN_RC_PERCENTAGE) {
        throw new Error(
          `Insufficient Resource Credits. You need at least ${MIN_RC_PERCENTAGE}% RC to delete posts.`
        )
      }

      // Delete the post
      const result = await deletePost({
        ...params,
        author: user.username,
        postingKey: user.postingKey,
      })

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete post')
      }

      // Refresh RC after deleting
      try {
        const rc = await getUserRC(user.username)
        updateRcPercentage(rc.percentage)
      } catch (error) {
        console.error('Failed to refresh RC:', error)
      }

      return result
    },
    onMutate: async () => {
      // T094: Optimistically remove post from queries
      await queryClient.cancelQueries({ queryKey: ['feed'] })
      await queryClient.cancelQueries({ queryKey: ['user-posts'] })

      // Store previous state for rollback
      const previousFeed = queryClient.getQueryData(['feed'])
      const previousUserPosts = queryClient.getQueryData(['user-posts'])

      return { previousFeed, previousUserPosts }
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousFeed) {
        queryClient.setQueryData(['feed'], context.previousFeed)
      }
      if (context?.previousUserPosts) {
        queryClient.setQueryData(['user-posts'], context.previousUserPosts)
      }
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['feed'] })
      queryClient.invalidateQueries({ queryKey: ['user-posts'] })
    },
  })
}

/**
 * Checks if user has sufficient RC to post
 */
export function useCheckRC() {
  const { user } = useAuthStore()

  return {
    hasEnoughRC: user ? user.rcPercentage >= MIN_RC_PERCENTAGE : false,
    rcPercentage: user?.rcPercentage || 0,
    minRequired: MIN_RC_PERCENTAGE,
  }
}
